import moment, { Moment } from "moment-timezone";
import { useStore } from "src/stores/Store";
import colors from "src/utils/colors";
import tw from "src/utils/tw";
import { Day } from "types/code-generator";
import { months } from "../../utils/constants";
import InputSelect, { InputOption } from "./InputSelect";

interface Props {
  value?: Date | "";
  endValue?: Date | "";
  range?: Boolean;
  onSelectDate?: (date: Date) => void;
  onChange?: (date: Date) => void;
  initialValue?: Date | "";
  minDate?: Date;
  maxDate?: Date;
  validDays?: Day[];
  utc?: boolean;
}

function isDisabled(day: Moment, minDate, maxDate, validDays) {
  if (minDate && day.isBefore(minDate)) {
    return true;
  }

  if (maxDate && day.isAfter(maxDate)) {
    return true;
  }

  if (validDays) {
    return !validDays.includes(day.format("dddd"));
  }

  return false;
}

export default function InputCalendar(props: Props) {
  const store = useStore();
  const today = moment();
  const value = props.utc ? moment.utc(props.value) : moment(props.value);
  const initialValue = moment(props.initialValue);
  const start = value.clone().startOf("month").startOf("week");
  const end = value.clone().endOf("month").endOf("week");
  const minDate = props.minDate ? moment(props.minDate) : null;
  const maxDate = props.maxDate ? moment(props.maxDate) : null;
  const calendar = [];

  let date = start.clone().subtract(1, "day");

  while (date.isBefore(end, "day")) {
    const days = [];

    for (let i = 0; i < 7; i++) {
      const newDate = date.clone().add(1, "day");
      date = date.add(1, "day");
      days.push(newDate);
    }

    calendar.push({
      days,
    });
  }

  // console.log("calendar", calendar);

  function renderHeader() {
    const year = value.get("year");
    const options = [];

    for (let i = year - 20; i < year + 10; i += 1) {
      options.push(
        <InputOption key={i} value={i}>
          {i}
        </InputOption>
      );
    }

    return (
      <div className="mb-4 flex">
        <div className="mr-2 flex-1">
          <InputSelect
            onChange={(newYear) => {
              const newDate = moment(props.value).set("year", newYear);
              props.onChange(newDate.toDate());
            }}
            value={value.get("year")}
          >
            {options}
          </InputSelect>
        </div>
        <div className="ml-2 flex-1">
          <InputSelect
            value={value.get("month")}
            onChange={(selectedMonth) => {
              const newDate = moment(props.value).set("month", selectedMonth);
              props.onChange(newDate.toDate());
            }}
          >
            {months.map((m) => {
              return (
                <InputOption key={m.value} value={m.value}>
                  {m.name}
                </InputOption>
              );
            })}
          </InputSelect>
        </div>
      </div>
    );
  }

  return (
    <div className="lg:mb-8">
      <div>{renderHeader()}</div>
      <div>
        <div className="flex">
          <div className="flex-1 text-center font-bold text-empty">Su</div>
          <div className="flex-1 text-center font-bold text-empty">Mo</div>
          <div className="flex-1 text-center font-bold text-empty">Tu</div>
          <div className="flex-1 text-center font-bold text-empty">We</div>
          <div className="flex-1 text-center font-bold text-empty">Th</div>
          <div className="flex-1 text-center font-bold text-empty">Fr</div>
          <div className="flex-1 text-center font-bold text-empty">Sa</div>
        </div>
        <div>
          {calendar.map((w, i) => {
            return (
              <div className="flex" key={i}>
                {w.days.map((d, i) => {
                  const disabled = isDisabled(
                    d,
                    minDate,
                    maxDate,
                    props.validDays
                  );
                  let active = initialValue && initialValue.isSame(d, "day");
                  let isBetween = false;

                  if (props.range) {
                    active =
                      props.initialValue && initialValue.isSame(d, "day");

                    if (!active && props.endValue) {
                      active = moment(props.endValue).isSame(d, "day");
                    }

                    isBetween = d.isBetween(initialValue, props.endValue);
                  }

                  const isToday =
                    today.isSame(d, "day") && today.isSame(d, "month");

                  return (
                    <button
                      disabled={disabled}
                      className={tw(
                        "p-0",
                        "flex-1",
                        "text-center",
                        "rounded",
                        "h-12",
                        "border",
                        "transition",
                        "truncate",
                        active && "text-white",
                        value && !value.isSame(d, "month") && !active
                          ? "text-empty"
                          : "",
                        disabled
                          ? "opacity-20"
                          : "cursor-pointer hover:bg-borderGray hover:text-dark"
                      )}
                      css={{
                        backgroundColor: active
                          ? store.theme.orgColor
                          : isBetween
                            ? colors.paleAction
                            : undefined,
                        borderColor: isToday ? store.theme.orgColor : "#fff",
                      }}
                      key={i}
                      onClick={() => props.onSelectDate(d.toDate())}
                    >
                      {d.get("date")}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
